import type { DictionariesResponse as ExternalDictionariesResponse } from "@synthesis/shared-services/external/dictionaries/types";
import type { DictionariesResponse as InternalDictionariesResponse } from "@synthesis/shared-services/internal/dictionaries/types";

import type { ApplicationSelectOption } from "../../constants";

export type ApplicationDictionariesResponse = ExternalDictionariesResponse | InternalDictionariesResponse;

export type CreateApplicationSelectOption = ApplicationSelectOption;

export type CreateApplicationDictionaries = {
  requestSubjects: CreateApplicationSelectOption[];
  plannedWorksTypes: CreateApplicationSelectOption[];
  adjoiningTypes: CreateApplicationSelectOption[];
  trackFundings: CreateApplicationSelectOption[];
  applicantTypes: CreateApplicationSelectOption[];
  railways: CreateApplicationSelectOption[];
};

export const EMPTY_CREATE_APPLICATION_DICTIONARIES: CreateApplicationDictionaries = {
  requestSubjects: [],
  plannedWorksTypes: [],
  adjoiningTypes: [],
  trackFundings: [],
  applicantTypes: [],
  railways: [],
};

/** Display order for radio «Тип заявителя / лица» (applicant + payer). */
const APPLICANT_TYPE_DISPLAY_ORDER = ["LEGAL_ENTITY", "SOLE_PROPRIETOR", "INDIVIDUAL"] as const;

const mapDictionaryItemsToSelectOptions = (items: ApplicationDictionariesResponse["railways"] | undefined): CreateApplicationSelectOption[] =>
  (items ?? [])
    .map((item) => {
      const code = item.code?.trim();
      const label = item.displayName?.trim();

      if (!code || !label) {
        return null;
      }

      return { value: code, label };
    })
    .filter((item): item is CreateApplicationSelectOption => item !== null);

const getApplicantTypeDisplayOrderIndex = (value: string): number => {
  const index = (APPLICANT_TYPE_DISPLAY_ORDER as readonly string[]).indexOf(value);
  return index === -1 ? Number.MAX_SAFE_INTEGER : index;
};

const sortApplicantTypeOptions = (options: CreateApplicationSelectOption[]): CreateApplicationSelectOption[] =>
  options.toSorted((left, right) => getApplicantTypeDisplayOrderIndex(left.value) - getApplicantTypeDisplayOrderIndex(right.value));

export const buildCreateApplicationDictionaries = (response: ApplicationDictionariesResponse | undefined): CreateApplicationDictionaries => ({
  requestSubjects: mapDictionaryItemsToSelectOptions(response?.requestSubjects),
  plannedWorksTypes: mapDictionaryItemsToSelectOptions(response?.plannedWorksTypes),
  adjoiningTypes: mapDictionaryItemsToSelectOptions(response?.adjoiningTypes),
  trackFundings: mapDictionaryItemsToSelectOptions(response?.trackFundings),
  applicantTypes: sortApplicantTypeOptions(mapDictionaryItemsToSelectOptions(response?.applicantTypes)),
  railways: mapDictionaryItemsToSelectOptions(response?.railways),
});

export const getDictionaryOptionLabel = (options: ApplicationSelectOption[], value: string | undefined): string => {
  if (!value?.trim()) {
    return "—";
  }

  return options.find((option) => option.value === value)?.label ?? value;
};

/**
 * Наименование дороги без хвоста «железная дорога».
 * Используется в полях, где метка уже содержит «Железная дорога»
 * (например, «Железная дорога (филиал РЖД)» на странице заявки),
 * чтобы название не продублировалось в значении.
 */
export const getRailwayShortName = (options: ApplicationSelectOption[], value: string | undefined): string =>
  getDictionaryOptionLabel(options, value).replace(/\s+железная дорога$/iu, "");

export const getApplicantTypeOptionsExcludingIndividual = (options: CreateApplicationSelectOption[]): CreateApplicationSelectOption[] =>
  options.filter((option) => option.value !== "INDIVIDUAL");
